import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Briefcase, Clock, Users, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CareerPage = () => {
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    cover_letter: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchPositions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/careers/positions`);
      setPositions(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching positions:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/careers/apply`, formData);
      toast.success('Application submitted successfully! We\'ll be in touch soon.');
      setFormData({ full_name: '', email: '', phone: '', position: '', experience: '', cover_letter: '' });
      setSelectedPosition(null);
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApply = (position) => {
    setSelectedPosition(position);
    setFormData({ ...formData, position: position.title });
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    { icon: Users, title: 'Great Team', description: 'Work with passionate logistics professionals' },
    { icon: Briefcase, title: 'Growth Opportunities', description: 'Career advancement and skill development' },
    { icon: Clock, title: 'Flexible Schedule', description: 'Remote options available for select roles' },
    { icon: MapPin, title: 'Nationwide Reach', description: 'Be part of a growing logistics network' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/67b0925b-7f4b-4cc2-900d-1c317dc2d0bd/images/dd0baf8fc28a2650877bb16f546fc3bd2e62b6be96a6836b45f6409c0beab942.png')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <span className="text-red-500 text-xs font-bold uppercase tracking-[0.2em]">Join Our Team</span>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-none tracking-tight mt-3 mb-6">
              Build Your Career<br />
              <span className="text-red-500">With Us</span>
            </h1>
            <p className="text-lg text-zinc-300 mb-8">
              Join Red Ball Express Logistics and be part of a team that's shaping the future of freight transportation.
            </p>
            <a 
              href="#positions"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wide transition-colors"
              data-testid="view-positions-btn"
            >
              View Open Positions
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-zinc-900">Why Work With Us?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="p-6 border border-zinc-200 text-center">
                <div className="w-14 h-14 bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-zinc-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="positions" className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 text-xs font-bold uppercase tracking-[0.2em]">Opportunities</span>
            <h2 className="text-3xl font-black text-zinc-900 mt-3">Open Positions</h2>
          </div>

          <div className="space-y-4">
            {positions.map((position) => (
              <div 
                key={position.id}
                className="bg-white border border-zinc-200 p-6 hover:border-zinc-300 transition-colors"
                data-testid={`position-${position.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{position.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {position.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {position.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {position.type}
                      </span>
                    </div>
                    <p className="text-zinc-600 mt-3">{position.description}</p>
                  </div>
                  <button
                    onClick={() => handleApply(position)}
                    className="bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-3 font-bold uppercase tracking-wide transition-colors flex-shrink-0"
                    data-testid={`apply-${position.id}`}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-50 p-8 lg:p-12 border border-zinc-200">
            <h2 className="text-2xl font-black text-zinc-900 mb-2">Apply Now</h2>
            {selectedPosition && (
              <p className="text-red-600 font-medium mb-6">
                Applying for: {selectedPosition.title}
              </p>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="John Doe"
                    data-testid="career-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="john@example.com"
                    data-testid="career-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none"
                    placeholder="(555) 123-4567"
                    data-testid="career-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    Position *
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none"
                    data-testid="career-position"
                  >
                    <option value="">Select a position</option>
                    {positions.map((pos) => (
                      <option key={pos.id} value={pos.title}>{pos.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  Years of Experience *
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none"
                  data-testid="career-experience"
                >
                  <option value="">Select experience level</option>
                  <option value="0-1">0-1 years</option>
                  <option value="1-3">1-3 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5+">5+ years</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  Cover Letter
                </label>
                <textarea
                  name="cover_letter"
                  value={formData.cover_letter}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none resize-none"
                  placeholder="Tell us about yourself and why you'd be a great fit..."
                  data-testid="career-cover"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wide transition-colors disabled:opacity-50 w-full"
                data-testid="career-submit"
              >
                {submitting ? 'Submitting...' : (
                  <>
                    Submit Application
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerPage;
